<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (applyErrorState && !getCardsList.length) || (applyErrorState && !getCardlessWidthdrawList.length)"
    :showLoader="showLoader"
    :errorStateMessage="t('cards.cardlessWithdraw.myCodes.errorStateMessage')"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :showTopRightAreaSlot="!!getCardsList.length"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Card Selector -->
        <template #accounts-card-slot-area>
            <selector-cards
            v-if="!!getCardsList.length"
            :selectedOption="selectedCardId"
            :options="getCardsList"
            @clicked="selectedCardId === $event ? null : [selectedCardId = $event, cardlessWithdrawalList(), showFeedbackToast = false]"/>
        </template>
        <!-- *********************************************
		*												 *
		*                      Content                   *
		*												 *
		********************************************** -->
        <transition-group name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState && !!getCardlessWidthdrawList.length" class="content-wrapper">
                <table-generic
                :headerData="headerTableData"
                :tableStyles="tableStyles"
                :tableData="tableData"
                @rowClicked="codeDetailClickHandler($event)"/>
            </div>
        </transition-group>
        <template #empty-state-slot>
            <!-- Empty-state -->
            <empty-state-generic
            v-if="!showLoader && !applyErrorState && (!getCardsList.length || !getCardlessWidthdrawList.length)"
            illustration="emptyState/cards-withdraw"
            :title = "t('cards.cardlessWithdraw.myCodes.emptyStateTitle')"
            :description = "t('cards.cardlessWithdraw.myCodes.emptyStateDescription')"/>
        </template>
    </wrapper-features-templates-one-column>
    <detail-modal
    v-if="showDetailModal"
    :selectedCardlessWithdraw="selectedCardlessWithdraw"
    :selectedCardId="selectedCardId"
    @cancelCardlessWithdraw="showCenteredModal = true"
    @hideMe="showDetailModal = false"/>
    <!-- Centered Modal -->
	<modal-centered-generic
	v-if="showCenteredModal"
    negative
    rightButtonIcon="check-line"
    :title="t('cards.cardlessWithdraw.myCodes.centeredModal.title')"
	:text="t('cards.cardlessWithdraw.myCodes.centeredModal.text')"
	:leftButtonText="t('cards.cardlessWithdraw.myCodes.centeredModal.leftButtonText')"
	:rightButtonText="t('cards.cardlessWithdraw.myCodes.centeredModal.rightButtonText')"
	@hideMe="showCenteredModal = false"
	@rightButtonClicked="requestCancelCardlessWithdrawCode"/>
    <!-- FeedbackToast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import SelectorCards from '@/components/generic/selectorsWithDetails/Cards'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import DetailModal from '@/components/features/cards/cardlessWithdraw/myCodes/DetailModal'

/* Assets & Helpers */
import { ref, computed, onBeforeUnmount } from 'vue'
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'
import { amountFormat } from '@/assets/scripts/amounts.js'
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { iconMapper, iconColorMapper } from '@/assets/scripts/mappers/cardlessWithdraw'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestCards, requestCardlessWidthdrawList, cancelCardlessWithdrawCode, clearFeatureStates } = mapActions(moduleMapper.CARDS_CARDLESS_WITHDRAW_MY_CODES)
const { getCardsList, getCardlessWidthdrawList } = mapGetters(moduleMapper.CARDS_CARDLESS_WITHDRAW_MY_CODES)

/* Data */
const showOverlayLoader = ref(false)
const showLoader = ref(false)
const applyErrorState = ref(false)
const showDetailModal = ref(false)
const showCenteredModal = ref(false)
const selectedCardId = ref(null)
const selectedCardlessWithdraw = ref(null)

/*************************************************
*                                                *
*                   Table Data                   *
*                                                *
*************************************************/
const headerTableData = [
    { label: t('cards.cardlessWithdraw.myCodes.tableHeaderData.amount'), showInMobile: { groupWith: ['creationDate'] }, dataAttr: 'amount', rowCellType: 'title' },
    { label: t('cards.cardlessWithdraw.myCodes.tableHeaderData.expiryDate'), dataAttr: 'expiryDate', rowCellType: 'date' },
    { label: t('cards.cardlessWithdraw.myCodes.tableHeaderData.creationDate'), dataAttr: 'creationDate', rowCellType: 'date' },
    { label: t('cards.cardlessWithdraw.myCodes.tableHeaderData.status'), showInMobile: true, dataAttr: 'status', rowCellType: 'status' }
]

const tableStyles = [
    { 'text-align': 'left', width: '150px', 'min-width': '150px' },
    { 'text-align': 'end', width: '100px', 'min-width': '100px' },
    { 'text-align': 'end', width: '100px', 'min-width': '100px' },
    { width: '15%', 'padding-left': '32px' }
]

const tableData = computed(() => {
    return getCardlessWidthdrawList.value.map(cardless => {
        return [
            { value: cardless.id, dataAttr: 'id' },
            { type: 'title', value: amountFormat(cardless.amount, cardless.currency), dataAttr: 'amount' },
            {
                value: {
                    content: cardless.expiryDate && cardless.status === 'ACTIVE' ? getDateWithTime(cardless.expiryDate) : '-',
                    classes: 'capitalize'
                },
                dataAttr: 'expiryDate'
            },
            {
                value: {
                    content: cardless.creationDate ? getDateWithTime(cardless.creationDate) : '-',
                    classes: 'capitalize'
                },
                dataAttr: 'creationDate'
            },
            {
                type: 'status',
                value: {
                    text: cardless.statusDescription,
                    icon: iconMapper(cardless.status),
                    classes: iconColorMapper(cardless.status)
                }
            }
        ]
    })
})

const codeDetailClickHandler = row => {
	selectedCardlessWithdraw.value = getCardlessWidthdrawList.value.find(cardless => cardless.id === row.rowId)

	showDetailModal.value = true
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestsCardList = async () => {
	showLoader.value = true
    applyErrorState.value = false

	try {
        await requestCards()
        if (getCardsList.value.length) {
            selectedCardId.value = getCardsList.value[0].id
            await cardlessWithdrawalList()
        }
		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, requestsCardList)
	}
}

const cardlessWithdrawalList = async () => {
    cancelAllPendingRequests()

    showLoader.value = true
    applyErrorState.value = false

    const payload = {
        cardId: selectedCardId.value
    }

    try {
        await requestCardlessWidthdrawList(payload)
        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, cardlessWithdrawalList)
    }
}

const requestCancelCardlessWithdrawCode = async () => {
    showOverlayLoader.value = true
	applyErrorState.value = false
	showDetailModal.value = false

    const payload = {
        cardId: selectedCardId.value,
        codeId: selectedCardlessWithdraw.value.id
    }

    try {
		const res = await cancelCardlessWithdrawCode(payload)
		showOverlayLoader.value = false
		showCenteredModal.value = false
		successHandler(res.data)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		errorHandler(error, requestCancelCardlessWithdrawCode)
	}
}

/*************************************************
 *                                                *
 *             Success/Error Handlers             *
 *                                                *
 *************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
	feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
    cardlessWithdrawalList()
}

const errorHandler = (response, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(response, retryFn)
	showFeedbackToast.value = true
}

requestsCardList()

onBeforeUnmount(() => {
	clearFeatureStates()
})

</script>

<style lang="scss" scoped>
.content-wrapper {
	@include commonEnteringAnimation;

    width: 100%;
}
</style>
